import { useState, useEffect, useRef, useMemo, useSyncExternalStore } from 'react'
import { Link } from 'react-router-dom'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner, Badge,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  Field, Input, Select, Textarea, Tooltip,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider,
  makeStyles, tokens, MessageBar, MessageBarBody,
} from '@fluentui/react-components'
import { Edit24Regular, Delete24Regular, ArrowUpload24Regular, ArrowDownload24Regular, DeleteDismiss24Regular, ChevronDownRegular, ArrowSortDownRegular, ArrowSortUpRegular, ArrowSortRegular, Add24Regular } from '@fluentui/react-icons'
import {
  absenceRequestsService,
  AbsenceRequest,
  AbsenceRequestStatus,
  ABSENCE_TYPES,
  DayHalf,
  DAY_HALF_LABEL,
} from '../../services/absenceRequestsService'
import { importsService, ImportResult } from '../../services/importsService'
import { exportsService } from '../../services/exportsService'
import { employeesService } from '../../services/employeesService'
import { vacationBalanceService } from '../../services/vacationBalanceService'
import { NativeDateInput } from '../../components/NativeDateInput'
import { MultiSelectFilter } from '../../components/MultiSelectFilter'
import VacationBalanceSummary from '../../components/VacationBalanceSummary'
import { useAuth } from '../../auth/useAuth'

const STATUS_COLOR: Record<number, 'warning' | 'success' | 'danger' | 'subtle'> = {
  1: 'warning', 2: 'success', 3: 'danger', 4: 'subtle',
}
const STATUS_LABEL: Record<number, string> = {
  1: 'Schvalováno', 2: 'Schváleno', 3: 'Zamítnuto', 4: 'Stornováno',
}
const ALL_STATUSES = ['1', '2', '3', '4']
const DEFAULT_STATUSES = ['1', '2', '3'] // výchozí: bez Stornovaných

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

type SortCol = 'employee' | 'startDate' | 'status' | 'requestedAt'

const useStyles = makeStyles({
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  filters:   { display: 'flex', gap: '12px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterCount: { display: 'flex', alignItems: 'center', marginBottom: '12px' },
  th: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3, textTransform: 'uppercase',
  },
  thSortable: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3, textTransform: 'uppercase',
    cursor: 'pointer', userSelect: 'none',
    ':hover': { color: tokens.colorNeutralForeground1 },
  },
  tableRow: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    cursor: 'default',
  },
  formGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fullRow:   { gridColumn: '1 / -1' },
  hint:      { fontSize: '11px', color: tokens.colorNeutralForeground3, marginTop: '2px' },
  actions:   { display: 'flex', gap: '4px' },
})

const emptyForm = {
  absenceTypeId: '',
  startDate: '',
  endDate: '',
  location: '',
  note: '',
  totalDays: '',
  totalHours: '',
  dayHalf: DayHalf.Full as DayHalf,
}

function parseDecimal(s: string): number {
  return Number(s.replace(',', '.'))
}

function parseApiError(err: any): string {
  const status = err?.response?.status
  if (status === 403) return 'Nemáte oprávnění k této operaci.'
  if (status === 409 && typeof err?.response?.data === 'string') return err.response.data
  if (err?.response?.data?.errors)
    return Object.values(err.response.data.errors).flat().join(', ')
  if (typeof err?.response?.data === 'string' && err.response.data.length < 1000)
    return err.response.data
  return 'Nastala chyba při ukládání.'
}

export default function AllRequestsPage() {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const { isHR, isAdmin } = useAuth()

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(DEFAULT_STATUSES)
  const [selectedTypes,    setSelectedTypes]    = useState<string[]>([])
  const typesInitialized = useRef(false)
  const [employeeFilter,   setEmployeeFilter]   = useState<string>('')
  const [filterMonth,      setFilterMonth]      = useState<string>('')   // '1'–'12'
  const [filterYear,       setFilterYear]       = useState<string>('')   // 'YYYY'
  const [sortCol,        setSortCol]        = useState<SortCol>('requestedAt')
  const [sortDir,        setSortDir]        = useState<'asc' | 'desc'>('desc')
  const [editingRequest, setEditingRequest] = useState<AbsenceRequest | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [totalDaysEdited, setTotalDaysEdited] = useState(false)
  const [formError, setFormError] = useState('')

  // HR nová žádost za zaměstnance
  const [hrNewOpen, setHrNewOpen] = useState(false)
  const [hrEmployeeSearch, setHrEmployeeSearch] = useState('')
  const [hrForm, setHrForm] = useState({ employeeId: '', absenceTypeId: '', startDate: '', endDate: '', location: '', note: '', totalDays: '', dayHalf: DayHalf.Full as DayHalf })
  const [hrTotalDaysEdited, setHrTotalDaysEdited] = useState(false)
  const [hrFormError, setHrFormError] = useState('')
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Export dialog — sdílený pro všechny 3 typy exportu
  type ExportType = 'attendance' | 'vacations' | 'paidLeave'
  const now = new Date()
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth()
  const prevYear  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  const [exportDialogType, setExportDialogType] = useState<ExportType | null>(null)
  const [exportMonth, setExportMonth] = useState(prevMonth)
  const [exportYear,  setExportYear]  = useState(prevYear)
  const [isExporting, setIsExporting] = useState(false)

  const { data: requests, isLoading, isError, refetch } = useQuery({
    queryKey: ['all-requests'],
    queryFn: () => absenceRequestsService.getAll(),
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  // Client-side filtrování + řazení
  const filtered = useMemo(() => {
    let result = requests ?? []

    if (employeeFilter.trim())
      result = result.filter(r => r.employeeFullName.toLowerCase().includes(employeeFilter.toLowerCase()))
    // [] = nic vybráno = žádný výsledek; neprázdný seznam = filtruj
    result = result.filter(r => selectedTypes.includes(String(r.absenceTypeId)))
    result = result.filter(r => selectedStatuses.includes(String(r.status)))
    if (filterMonth && filterYear) {
      const y = Number(filterYear), m = Number(filterMonth)
      const first = `${y}-${String(m).padStart(2, '0')}-01`
      const last  = `${y}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
      result = result.filter(r => r.startDate <= last && r.endDate >= first)
    }

    return [...result].sort((a, b) => {
      const va = sortCol === 'employee' ? a.employeeFullName
               : sortCol === 'startDate' ? a.startDate
               : sortCol === 'status' ? String(a.status)
               : a.requestedAt
      const vb = sortCol === 'employee' ? b.employeeFullName
               : sortCol === 'startDate' ? b.startDate
               : sortCol === 'status' ? String(b.status)
               : b.requestedAt
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va)
    })
  }, [requests, employeeFilter, selectedTypes, selectedStatuses, filterMonth, filterYear, sortCol, sortDir])

  const absenceTypes = ABSENCE_TYPES

  // Při prvním načtení typů nastavit výchozí výběr = vše vybráno
  useEffect(() => {
    if (absenceTypes && absenceTypes.length > 0 && !typesInitialized.current) {
      typesInitialized.current = true
      setSelectedTypes(absenceTypes.map(at => String(at.id)))
    }
  }, [absenceTypes])

  // HR nová žádost — seznam zaměstnanců
  const { data: hrEmployees = [] } = useQuery({
    queryKey: ['employees-hr-picker'],
    queryFn: () => employeesService.getAll({ status: 1, pageSize: 500 }).then(r => r.items),
    enabled: hrNewOpen,
    staleTime: 60_000,
  })

  // HR nová žádost — zůstatek dovolené vybraného zaměstnance
  const { data: hrBalance } = useQuery({
    queryKey: ['vacation-balance', Number(hrForm.employeeId), new Date().getFullYear()],
    queryFn: () => vacationBalanceService.getBalance(Number(hrForm.employeeId)),
    enabled: hrNewOpen && !!hrForm.employeeId,
  })

  // HR nová žádost — pracovní dny dle rozvrhu vybraného zaměstnance
  const { data: hrServerDays } = useQuery({
    queryKey: ['working-days', hrForm.startDate, hrForm.endDate, hrForm.employeeId],
    queryFn: () => absenceRequestsService.getWorkingDays(hrForm.startDate, hrForm.endDate, Number(hrForm.employeeId)),
    enabled: hrNewOpen && !!hrForm.employeeId && !!hrForm.startDate && !!hrForm.endDate && !hrTotalDaysEdited,
    staleTime: 0,
  })

  useEffect(() => {
    if (hrServerDays != null && !hrTotalDaysEdited)
      setHrForm(f => ({ ...f, totalDays: hrServerDays > 0 ? String(hrServerDays).replace('.', ',') : '' }))
  }, [hrServerDays, hrTotalDaysEdited])

  const filteredHrEmployees = hrEmployees
    .filter(e => !hrEmployeeSearch || e.fullName.toLowerCase().includes(hrEmployeeSearch.toLowerCase()))
    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'cs'))

  const resetHrDialog = () => {
    setHrNewOpen(false)
    setHrForm({ employeeId: '', absenceTypeId: '', startDate: '', endDate: '', location: '', note: '', totalDays: '', dayHalf: DayHalf.Full as DayHalf })
    setHrTotalDaysEdited(false)
    setHrFormError('')
    setHrEmployeeSearch('')
  }

  const hrCreateMutation = useMutation({
    mutationFn: () => absenceRequestsService.create({
      employeeId:    Number(hrForm.employeeId),
      absenceTypeId: Number(hrForm.absenceTypeId),
      startDate:     hrForm.startDate,
      endDate:       hrForm.endDate,
      location:      hrForm.location,
      note:          hrForm.note || undefined,
      totalDays:     hrTotalDaysEdited && hrForm.totalDays ? parseDecimal(hrForm.totalDays) : undefined,
      dayHalf:       hrForm.dayHalf,
    }),
    onSuccess: () => { invalidate(); resetHrDialog() },
    onError:   (err: any) => setHrFormError(parseApiError(err)),
  })

  const handleHrSubmit = () => {
    setHrFormError('')
    if (!hrForm.employeeId)    { setHrFormError('Vyberte zaměstnance.'); return }
    if (!hrForm.absenceTypeId) { setHrFormError('Vyberte typ nepřítomnosti.'); return }
    if (!hrForm.startDate)     { setHrFormError('Zadejte datum od.'); return }
    if (!hrForm.endDate)       { setHrFormError('Zadejte datum do.'); return }
    if (hrForm.endDate < hrForm.startDate) { setHrFormError('Datum do nemůže být dříve než datum od.'); return }
    if (!hrForm.location.trim()) { setHrFormError('Zadejte místo pobytu.'); return }
    if (!hrForm.totalDays || parseDecimal(hrForm.totalDays) <= 0) { setHrFormError('Počet dnů musí být větší než 0.'); return }
    if (hrIsHalf && !hrIsSingleDay) { setHrFormError('Půl dne lze zadat jen na jeden den.'); return }
    if (!hrIsHalf && parseDecimal(hrForm.totalDays) % 1 !== 0) { setHrFormError('Zlomkové vícedenní volno nelze — rozdělte na celé dny a půlden.'); return }
    hrCreateMutation.mutate()
  }

  // Počet pracovních dnů ze serveru (respektuje DB svátky) — při editaci a změně datumů
  const { data: serverDaysEdit } = useQuery({
    queryKey: ['working-days', form.startDate, form.endDate, editingRequest?.employeeId],
    queryFn: () => absenceRequestsService.getWorkingDays(form.startDate, form.endDate, editingRequest!.employeeId),
    enabled: !!editingRequest && !!form.startDate && !!form.endDate && !totalDaysEdited,
    staleTime: 0,
  })

  useEffect(() => {
    if (serverDaysEdit != null && !totalDaysEdited) {
      setForm(f => ({ ...f, totalDays: serverDaysEdit > 0 ? String(serverDaysEdit).replace('.', ',') : '' }))
    }
  }, [serverDaysEdit, totalDaysEdited])

  // #11 — přepínač Celý den / Půl dne pro edit (form) i HR create (hrForm)
  const isSingleDay = !!form.startDate && !!form.endDate && form.startDate === form.endDate
  const isHalf = form.dayHalf !== DayHalf.Full
  const hrIsSingleDay = !!hrForm.startDate && !!hrForm.endDate && hrForm.startDate === hrForm.endDate
  const hrIsHalf = hrForm.dayHalf !== DayHalf.Full

  useEffect(() => {
    if (!isSingleDay && form.dayHalf !== DayHalf.Full) { setForm(f => ({ ...f, dayHalf: DayHalf.Full })); setTotalDaysEdited(false) }
  }, [isSingleDay]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hrIsSingleDay && hrForm.dayHalf !== DayHalf.Full) { setHrForm(f => ({ ...f, dayHalf: DayHalf.Full })); setHrTotalDaysEdited(false) }
  }, [hrIsSingleDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const setFullDay = () => { setTotalDaysEdited(true); setForm(f => ({ ...f, dayHalf: DayHalf.Full, totalDays: '1' })) }
  const setHalf = (dh: DayHalf) => { setTotalDaysEdited(true); setForm(f => ({ ...f, dayHalf: dh, totalDays: '0,5' })) }
  const adjustWholeDays = (d: number) => { setTotalDaysEdited(true); setForm(f => ({ ...f, totalDays: String(Math.max(1, Math.round((parseDecimal(f.totalDays) || 0) + d))) })) }

  const hrSetFullDay = () => { setHrTotalDaysEdited(true); setHrForm(f => ({ ...f, dayHalf: DayHalf.Full, totalDays: '1' })) }
  const hrSetHalf = (dh: DayHalf) => { setHrTotalDaysEdited(true); setHrForm(f => ({ ...f, dayHalf: dh, totalDays: '0,5' })) }
  const hrAdjustWholeDays = (d: number) => { setHrTotalDaysEdited(true); setHrForm(f => ({ ...f, totalDays: String(Math.max(1, Math.round((parseDecimal(f.totalDays) || 0) + d))) })) }

  const set = (field: keyof typeof emptyForm) => (e: any) =>
    setForm(f => ({ ...f, [field]: e.target?.value ?? e }))

  const openEditDialog = (r: AbsenceRequest) => {
    setEditingRequest(r)
    setForm({
      absenceTypeId: String(r.absenceTypeId),
      startDate:     r.startDate,
      endDate:       r.endDate,
      location:      r.location ?? '',
      note:          r.note ?? '',
      totalDays:     String(r.totalDays).replace('.', ','),
      totalHours:    String(r.totalHours).replace('.', ','),
      dayHalf:       r.dayHalf,
    })
    setTotalDaysEdited(true)
    setFormError('')
  }

  const resetDialog = () => {
    setEditingRequest(null)
    setForm(emptyForm)
    setTotalDaysEdited(false)
    setFormError('')
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['all-requests'] })
    // #26: po úpravě žádosti obnovit i zůstatek dovolené (dlaždice + ⚠ varování na kartě)
    queryClient.invalidateQueries({ queryKey: ['vacation-balance'] })
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      if (exportDialogType === 'attendance') await exportsService.downloadAttendance(exportYear, exportMonth)
      else if (exportDialogType === 'vacations') await exportsService.downloadApprovedVacations(exportYear, exportMonth)
      else if (exportDialogType === 'paidLeave') await exportsService.downloadApprovedPaidLeave(exportYear, exportMonth)
      setExportDialogType(null)
    } finally {
      setIsExporting(false)
    }
  }

  const importMutation = useMutation({
    mutationFn: (file: File) => importsService.importAbsences(file),
    onSuccess: (result) => { setImportResult(result); invalidate() },
    onError: () => alert('Import se nezdařil.'),
  })

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) importMutation.mutate(file)
    e.target.value = ''
  }

  const updateMutation = useMutation({
    mutationFn: () => absenceRequestsService.update(editingRequest!.id, {
      absenceTypeId: Number(form.absenceTypeId),
      startDate:  form.startDate,
      endDate:    form.endDate,
      location:   form.location,
      note:       form.note || undefined,
      // Pokud HR ručně nezměnil počet dnů, necháme server přepočítat z DB svátků.
      totalDays:  totalDaysEdited ? (form.totalDays ? parseDecimal(form.totalDays) : undefined) : undefined,
      dayHalf:    form.dayHalf,
      totalHours: (isHR || isAdmin) && form.totalHours ? parseDecimal(form.totalHours) : undefined,
    }),
    onSuccess: () => { invalidate(); resetDialog() },
    onError:   (err: any) => setFormError(parseApiError(err)),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: number) => absenceRequestsService.cancel(id),
    onSuccess: () => invalidate(),
    onError:   () => alert('Stornování se nezdařilo.'),
  })

  const deletePermanentMutation = useMutation({
    mutationFn: (id: number) => absenceRequestsService.deletePermanent(id),
    onSuccess: () => invalidate(),
    onError:   () => alert('Odstranění se nezdařilo.'),
  })

  const handleSubmit = () => {
    setFormError('')
    if (!form.absenceTypeId) { setFormError('Vyberte typ nepřítomnosti.'); return }
    if (!form.startDate)     { setFormError('Zadejte datum od.'); return }
    if (!form.endDate)       { setFormError('Zadejte datum do.'); return }
    if (form.endDate < form.startDate) { setFormError('Datum do nemůže být dříve než datum od.'); return }
    if (!form.location)      { setFormError('Zadejte místo pobytu.'); return }
    if (!form.totalDays || parseDecimal(form.totalDays) <= 0) { setFormError('Počet dnů musí být větší než 0.'); return }
    if (isHalf && !isSingleDay) { setFormError('Půl dne lze zadat jen na jeden den.'); return }
    if (!isHalf && parseDecimal(form.totalDays) % 1 !== 0) { setFormError('Zlomkové vícedenní volno nelze — rozdělte na celé dny a půlden.'); return }
    updateMutation.mutate()
  }

  const handleCancel = (r: AbsenceRequest) => {
    if (!confirm(`Opravdu stornovat žádost ${r.employeeFullName} — ${r.absenceTypeName} (${r.startDate} – ${r.endDate})?`)) return
    cancelMutation.mutate(r.id)
  }

  const handleDeletePermanent = (r: AbsenceRequest) => {
    const statusLabel = r.status === AbsenceRequestStatus.Rejected ? 'zamítnutou' : 'stornovanou'
    if (!confirm(`Trvale odstranit ${statusLabel} žádost ${r.employeeFullName} — ${r.absenceTypeName} (${r.startDate} – ${r.endDate})?\n\nTato akce je nevratná.`)) return
    deletePermanentMutation.mutate(r.id)
  }

  const canDeletePermanent = (r: AbsenceRequest) =>
    (isHR || isAdmin) &&
    (r.status === AbsenceRequestStatus.Cancelled || r.status === AbsenceRequestStatus.Rejected)

  // HR vidí akce pro Pending i Approved
  const canEdit = (r: AbsenceRequest) =>
    r.status === AbsenceRequestStatus.Pending || r.status === AbsenceRequestStatus.Approved

  const typeOptions = absenceTypes?.map(at => ({ value: String(at.id), label: at.name })) ?? []
  const statusOptions = ALL_STATUSES.map(s => ({ value: s, label: STATUS_LABEL[Number(s)] }))

  const isDefaultStatuses = selectedStatuses.length === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every(s => selectedStatuses.includes(s))
  const allTypeCount = absenceTypes?.length ?? 0
  const hasActiveTypeFilter = selectedTypes.length > 0 && selectedTypes.length < allTypeCount
  const hasActiveFilter = !!employeeFilter.trim() || !!(filterMonth && filterYear) ||
    hasActiveTypeFilter || !isDefaultStatuses

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className={styles.header}>
        <Text size={600} weight="semibold">Všechny žádosti</Text>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button icon={<ChevronDownRegular />} iconPosition="after">
                Export &amp; Import
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<ArrowDownload24Regular />} onClick={() => setExportDialogType('attendance')}>
                  Export docházky…
                </MenuItem>
                <MenuItem icon={<ArrowDownload24Regular />} onClick={() => setExportDialogType('vacations')}>
                  Export schválených dovolených…
                </MenuItem>
                <MenuItem icon={<ArrowDownload24Regular />} onClick={() => setExportDialogType('paidLeave')}>
                  Export schváleného placeného volna…
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={importMutation.isPending ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
                  disabled={importMutation.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Import žádostí z D365
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          {(isHR || isAdmin) && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => setHrNewOpen(true)}>
              Nová žádost za zaměstnance
            </Button>
          )}
        </div>
      </div>

      {/* Filtry */}
      <div className={styles.filters}>
        <Field label="Zaměstnanec">
          <Input
            value={employeeFilter}
            onChange={e => setEmployeeFilter(e.target.value)}
            placeholder="Hledat…"
            style={{ minWidth: 160 }}
          />
        </Field>

        {/* Typ — multi-select */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Text size={200} style={{ fontWeight: 600, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: '11px' }}>Typ</Text>
          <MultiSelectFilter
            label="Typ"
            options={typeOptions}
            value={selectedTypes}
            onChange={setSelectedTypes}
          />
        </div>

        {/* Stav — multi-select */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Text size={200} style={{ fontWeight: 600, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: '11px' }}>Stav</Text>
          <MultiSelectFilter
            label="Stav"
            options={statusOptions}
            value={selectedStatuses}
            onChange={setSelectedStatuses}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Text size={200} style={{ fontWeight: 600, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: '11px' }}>Měsíc</Text>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ minWidth: 110 }}>
              <option value="">— měsíc —</option>
              {['Leden','Únor','Březen','Duben','Květen','Červen',
                'Červenec','Srpen','Září','Říjen','Listopad','Prosinec']
                .map((name, i) => <option key={i+1} value={i+1}>{name}</option>)}
            </Select>
            <Select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ minWidth: 80 }}>
              <option value="">— rok —</option>
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 1 + i)
                .map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </div>
        </div>

        {hasActiveFilter && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Text size={200} style={{ fontSize: '11px' }}>&nbsp;</Text>
            <Button appearance="subtle" size="small" onClick={() => {
              setEmployeeFilter('')
              setFilterMonth('')
              setFilterYear('')
              setSelectedTypes(absenceTypes?.map(at => String(at.id)) ?? [])
              setSelectedStatuses([...DEFAULT_STATUSES])
            }}>
              Zrušit filtry
            </Button>
          </div>
        )}
      </div>
      <div className={styles.filterCount}>
        <div style={{ flex: 1 }} />
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Celkem: {filtered.length}
        </Text>
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst žádosti o nepřítomnost."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {isLoading ? (
        <Spinner label="Načítám…" style={{ margin: '32px auto' }} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="sticky-head">
              <TableHeaderCell className={styles.thSortable} onClick={() => { setSortCol('employee'); setSortDir(s => sortCol === 'employee' ? (s === 'asc' ? 'desc' : 'asc') : 'asc') }}>
                Zaměstnanec {sortCol === 'employee' ? (sortDir === 'asc' ? <ArrowSortUpRegular style={{ fontSize: 12 }} /> : <ArrowSortDownRegular style={{ fontSize: 12 }} />) : <ArrowSortRegular style={{ fontSize: 12, opacity: 0.3 }} />}
              </TableHeaderCell>
              <TableHeaderCell className={styles.th}>Typ</TableHeaderCell>
              <TableHeaderCell className={styles.thSortable} onClick={() => { setSortCol('startDate'); setSortDir(s => sortCol === 'startDate' ? (s === 'asc' ? 'desc' : 'asc') : 'asc') }}>
                Od {sortCol === 'startDate' ? (sortDir === 'asc' ? <ArrowSortUpRegular style={{ fontSize: 12 }} /> : <ArrowSortDownRegular style={{ fontSize: 12 }} />) : <ArrowSortRegular style={{ fontSize: 12, opacity: 0.3 }} />}
              </TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th}>Do</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th}>Dní / hodin</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th}>Místo pobytu</TableHeaderCell>}
              <TableHeaderCell className={styles.thSortable} onClick={() => { setSortCol('status'); setSortDir(s => sortCol === 'status' ? (s === 'asc' ? 'desc' : 'asc') : 'asc') }}>
                Stav {sortCol === 'status' ? (sortDir === 'asc' ? <ArrowSortUpRegular style={{ fontSize: 12 }} /> : <ArrowSortDownRegular style={{ fontSize: 12 }} />) : <ArrowSortRegular style={{ fontSize: 12, opacity: 0.3 }} />}
              </TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.thSortable} onClick={() => { setSortCol('requestedAt'); setSortDir(s => sortCol === 'requestedAt' ? (s === 'asc' ? 'desc' : 'asc') : 'desc') }}>
                Podáno {sortCol === 'requestedAt' ? (sortDir === 'asc' ? <ArrowSortUpRegular style={{ fontSize: 12 }} /> : <ArrowSortDownRegular style={{ fontSize: 12 }} />) : <ArrowSortRegular style={{ fontSize: 12, opacity: 0.3 }} />}
              </TableHeaderCell>}
              {!isMobile && <TableHeaderCell></TableHeaderCell>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id} className={styles.tableRow}>
                <TableCell>
                  {(isHR || isAdmin) ? (
                    <Link
                      to={`/employees/${r.employeeId}`}
                      style={{ color: tokens.colorBrandForeground1, textDecoration: 'none' }}
                      onClick={e => e.stopPropagation()}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      title="Otevřít kartu zaměstnance (Ctrl+klik = nová karta)"
                    >
                      {r.employeeFullName}
                    </Link>
                  ) : r.employeeFullName}
                  {r.workloadMismatch && (
                    <Tooltip relationship="label" content="Počet hodin této dovolené nemusí odpovídat úvazku platnému v jejím termínu (úvazek se v roce měnil). Zkontrolujte a případně ručně opravte pole Čerpané hodiny.">
                      <span style={{ color: tokens.colorStatusWarningForeground1, marginLeft: 6, cursor: 'help', fontWeight: 700 }}>⚠</span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{r.absenceTypeName}</TableCell>
                <TableCell>{new Date(r.startDate).toLocaleDateString('cs-CZ')}</TableCell>
                {!isMobile && <TableCell>{new Date(r.endDate).toLocaleDateString('cs-CZ')}</TableCell>}
                {!isMobile && <TableCell>{String(r.totalDays).replace('.', ',')}{r.dayHalf !== DayHalf.Full ? ` (${DAY_HALF_LABEL[r.dayHalf]})` : ''} / {String(r.totalHours).replace('.', ',')} h</TableCell>}
                {!isMobile && <TableCell>{r.location ?? '–'}</TableCell>}
                <TableCell>
                  <Badge color={STATUS_COLOR[r.status] ?? 'subtle'}>
                    {STATUS_LABEL[r.status] ?? r.status}
                  </Badge>
                </TableCell>
                {!isMobile && <TableCell>{new Date(r.requestedAt).toLocaleDateString('cs-CZ')}</TableCell>}
                {!isMobile && (
                  <TableCell onClick={e => e.stopPropagation()}>
                    <div className={styles.actions}>
                      {canEdit(r) && (<>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Edit24Regular />}
                          title="Upravit"
                          onClick={() => openEditDialog(r)}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Delete24Regular />}
                          title="Stornovat"
                          disabled={cancelMutation.isPending}
                          onClick={() => handleCancel(r)}
                        />
                      </>)}
                      {canDeletePermanent(r) && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<DeleteDismiss24Regular />}
                          title="Trvale odstranit"
                          disabled={deletePermanentMutation.isPending}
                          style={{ color: tokens.colorPaletteRedForeground1 }}
                          onClick={() => handleDeletePermanent(r)}
                        />
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={isMobile ? 4 : 9} style={{ textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                  Žádné záznamy
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      </div>

      {/* Dialog výsledku importu */}
      <Dialog open={!!importResult} onOpenChange={(_, d) => { if (!d.open) setImportResult(null) }}>
        <DialogSurface style={{ maxWidth: 560 }}>
          <DialogTitle>Výsledek importu žádostí</DialogTitle>
          <DialogBody>
            {importResult && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <MessageBar intent={importResult.skipped > 0 ? 'warning' : 'success'}>
                  <MessageBarBody>
                    Vytvořeno: <strong>{importResult.created}</strong> &nbsp;|&nbsp;
                    Aktualizováno: <strong>{importResult.updated}</strong> &nbsp;|&nbsp;
                    Přeskočeno: <strong>{importResult.skipped}</strong>
                  </MessageBarBody>
                </MessageBar>
                {importResult.rows.length > 0 && (
                  <div style={{ maxHeight: 320, overflowY: 'auto', fontSize: 13 }}>
                    {importResult.rows.map(r => (
                      <div key={r.rowNumber} style={{ marginBottom: 6 }}>
                        <Text weight="semibold">Řádek {r.rowNumber} — {r.name}</Text>
                        {r.messages.map((m, i) => (
                          <div key={i} style={{
                            color: m.type === 'error'
                              ? tokens.colorPaletteRedForeground1
                              : tokens.colorPaletteYellowForeground1,
                            marginLeft: 12,
                          }}>
                            {m.type === 'error' ? '✗' : '⚠'} {m.text}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogBody>
          <DialogActions>
            <Button appearance="primary" onClick={() => setImportResult(null)}>Zavřít</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog editace */}
      <Dialog open={!!editingRequest} onOpenChange={(_, d) => { if (!d.open) resetDialog() }}>
        <DialogSurface style={{ maxWidth: 560 }}>
          <DialogTitle>
            Upravit žádost — {editingRequest?.employeeFullName}
          </DialogTitle>
          <DialogBody>
            <div className={styles.formGrid}>
              {formError && (
                <div className={styles.fullRow}>
                  <MessageBar intent="error"><MessageBarBody>{formError}</MessageBarBody></MessageBar>
                </div>
              )}

              <div className={styles.fullRow}>
                <Field label="Typ nepřítomnosti" required>
                  <Select value={form.absenceTypeId} onChange={set('absenceTypeId')}>
                    <option value="">– vyberte –</option>
                    {absenceTypes?.map(at => (
                      <option key={at.id} value={at.id}>{at.name}</option>
                    ))}
                  </Select>
                </Field>
                {absenceTypes?.find(at => String(at.id) === form.absenceTypeId)?.code === 'PAID_SPECIAL' && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                    Placené volno (svatba, pohřeb, narození dítěte, doktor, doprovod, darování krve a krevní plazmy…)
                  </div>
                )}
              </div>

              <Field label="Datum od" required>
                <NativeDateInput value={form.startDate} onChange={v => { setForm(f => ({ ...f, startDate: v })); setTotalDaysEdited(false) }} />
              </Field>

              <Field label="Datum do" required>
                <NativeDateInput value={form.endDate} onChange={v => { setForm(f => ({ ...f, endDate: v })); setTotalDaysEdited(false) }} min={form.startDate || undefined} />
              </Field>

              {isSingleDay ? (
                <Field label="Rozsah" required>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button appearance={form.dayHalf === DayHalf.Full ? 'primary' : 'secondary'} onClick={setFullDay}>Celý den</Button>
                    <Button appearance={form.dayHalf === DayHalf.Morning ? 'primary' : 'secondary'} onClick={() => setHalf(DayHalf.Morning)}>Dopoledne</Button>
                    <Button appearance={form.dayHalf === DayHalf.Afternoon ? 'primary' : 'secondary'} onClick={() => setHalf(DayHalf.Afternoon)}>Odpoledne</Button>
                  </div>
                </Field>
              ) : (
                <Field label="Počet pracovních dnů" required>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button size="small" onClick={() => adjustWholeDays(-1)} disabled={(parseDecimal(form.totalDays) || 0) <= 1}>−</Button>
                    <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{form.totalDays || '—'}</span>
                    <Button size="small" onClick={() => adjustWholeDays(1)}>+</Button>
                  </div>
                  <div className={styles.hint}>
                    {totalDaysEdited ? 'Ručně upraveno' : 'Automaticky vypočteno (bez svátků a víkendů)'}
                  </div>
                </Field>
              )}

              <Field label="Místo pobytu" required className={styles.fullRow}>
                <Input value={form.location} onChange={set('location')} />
              </Field>

              <Field label="Poznámka" className={styles.fullRow}>
                <Textarea value={form.note} onChange={set('note')} rows={2} />
              </Field>

              {(isHR || isAdmin) && (
                <Field label="Čerpané hodiny (h)" className={styles.fullRow}>
                  <Input type="text" inputMode="decimal" value={form.totalHours}
                    placeholder="např. 12 nebo 3,5"
                    onChange={e => setForm(f => ({ ...f, totalHours: (e.target?.value ?? '').replace(/[^0-9.,]/g, '') }))} />
                  <div className={styles.hint}>
                    Úvazek v době čerpání (ruční oprava). Prázdné = dopočítat z dnů × aktuální úvazek.
                  </div>
                </Field>
              )}
            </div>
          </DialogBody>
          <DialogActions>
            <Button appearance="secondary" onClick={resetDialog}>Zrušit</Button>
            <Button
              appearance="primary"
              disabled={updateMutation.isPending}
              onClick={handleSubmit}
            >
              {updateMutation.isPending ? 'Ukládám…' : 'Uložit změny'}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog: HR — Nová žádost za zaměstnance */}
      <Dialog open={hrNewOpen} onOpenChange={(_, d) => { if (!d.open) resetHrDialog() }}>
        <DialogSurface style={{ width: '560px', maxWidth: '95vw' }}>
          <DialogTitle>Nová žádost za zaměstnance</DialogTitle>
          <DialogBody>
          <DialogContent>
            <div className={styles.formGrid}>
              {hrFormError && (
                <div className={styles.fullRow}>
                  <MessageBar intent="error"><MessageBarBody>{hrFormError}</MessageBarBody></MessageBar>
                </div>
              )}

              {/* Výběr zaměstnance — hledat + dropdown vedle sebe */}
              <div className={styles.fullRow} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <Field label="Hledat zaměstnance" style={{ width: '180px', flexShrink: 0 }}>
                  <Input
                    placeholder="Jméno…"
                    value={hrEmployeeSearch}
                    onChange={e => setHrEmployeeSearch(e.target.value)}
                  />
                </Field>
                <Field label="Zaměstnanec" required style={{ flex: 1 }}>
                  <Select
                    value={hrForm.employeeId}
                    onChange={e => {
                      setHrForm(f => ({ ...f, employeeId: e.target.value, totalDays: '' }))
                      setHrTotalDaysEdited(false)
                    }}
                  >
                    <option value="">– vyberte –</option>
                    {filteredHrEmployees.map(e => (
                      <option key={e.id} value={e.id}>{e.fullName}</option>
                    ))}
                  </Select>
                </Field>
              </div>

              {/* Zůstatek dovolené vybraného zaměstnance */}
              {hrForm.employeeId && hrBalance && (
                <div className={styles.fullRow}>
                  <VacationBalanceSummary employeeId={Number(hrForm.employeeId)} compact />
                </div>
              )}

              {/* Formulář žádosti */}
              <div className={styles.fullRow}>
                <Field label="Typ nepřítomnosti" required>
                  <Select value={hrForm.absenceTypeId} onChange={e => setHrForm(f => ({ ...f, absenceTypeId: e.target.value }))}>
                    <option value="">– vyberte –</option>
                    {ABSENCE_TYPES.map(at => (
                      <option key={at.id} value={at.id}>{at.name}</option>
                    ))}
                  </Select>
                </Field>
                {ABSENCE_TYPES.find(at => String(at.id) === hrForm.absenceTypeId)?.code === 'PAID_SPECIAL' && (
                  <div className={styles.hint} style={{ marginTop: 4 }}>
                    Placené volno (svatba, pohřeb, narození dítěte, doktor, doprovod, darování krve…)
                  </div>
                )}
              </div>

              <Field label="Datum od" required>
                <NativeDateInput value={hrForm.startDate} onChange={v => { setHrForm(f => ({ ...f, startDate: v })); setHrTotalDaysEdited(false) }} />
              </Field>

              <Field label="Datum do" required>
                <NativeDateInput value={hrForm.endDate} min={hrForm.startDate || undefined} onChange={v => { setHrForm(f => ({ ...f, endDate: v })); setHrTotalDaysEdited(false) }} />
              </Field>

              {hrIsSingleDay ? (
                <Field label="Rozsah" required>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button appearance={hrForm.dayHalf === DayHalf.Full ? 'primary' : 'secondary'} onClick={hrSetFullDay}>Celý den</Button>
                    <Button appearance={hrForm.dayHalf === DayHalf.Morning ? 'primary' : 'secondary'} onClick={() => hrSetHalf(DayHalf.Morning)}>Dopoledne</Button>
                    <Button appearance={hrForm.dayHalf === DayHalf.Afternoon ? 'primary' : 'secondary'} onClick={() => hrSetHalf(DayHalf.Afternoon)}>Odpoledne</Button>
                  </div>
                </Field>
              ) : (
                <Field label="Počet pracovních dnů" required>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button size="small" onClick={() => hrAdjustWholeDays(-1)} disabled={(parseDecimal(hrForm.totalDays) || 0) <= 1}>−</Button>
                    <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{hrForm.totalDays || '—'}</span>
                    <Button size="small" onClick={() => hrAdjustWholeDays(1)}>+</Button>
                  </div>
                  <div className={styles.hint}>
                    {hrTotalDaysEdited ? 'Ručně upraveno' : 'Automaticky vypočteno (bez svátků a víkendů)'}
                  </div>
                </Field>
              )}

              <Field label="Místo pobytu" required className={styles.fullRow}>
                <Input value={hrForm.location} onChange={e => setHrForm(f => ({ ...f, location: e.target.value }))} />
              </Field>

              <Field label="Poznámka" className={styles.fullRow}>
                <Textarea rows={2} value={hrForm.note} onChange={(_, d) => setHrForm(f => ({ ...f, note: d.value }))} />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={resetHrDialog}>Zrušit</Button>
            <Button appearance="primary" disabled={hrCreateMutation.isPending} onClick={handleHrSubmit}>
              {hrCreateMutation.isPending ? 'Ukládám…' : 'Podat žádost'}
            </Button>
          </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Sdílený dialog pro exporty */}
      <Dialog open={!!exportDialogType} onOpenChange={(_, s) => { if (!s.open) setExportDialogType(null) }}>
        <DialogSurface style={{ maxWidth: 360 }}>
          <DialogTitle>
            {exportDialogType === 'attendance' && 'Export docházky'}
            {exportDialogType === 'vacations'  && 'Export schválených dovolených'}
            {exportDialogType === 'paidLeave'  && 'Export schváleného placeného volna'}
          </DialogTitle>
          <DialogBody>
            <DialogContent>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <Select value={String(exportMonth)} onChange={e => setExportMonth(Number(e.target.value))} style={{ flex: 1 }}>
                  {['Leden','Únor','Březen','Duben','Květen','Červen',
                    'Červenec','Srpen','Září','Říjen','Listopad','Prosinec']
                    .map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </Select>
                <Select value={String(exportYear)} onChange={e => setExportYear(Number(e.target.value))} style={{ width: 96 }}>
                  {Array.from({ length: 3 }, (_, i) => now.getFullYear() - 1 + i)
                    .map(y => <option key={y} value={y}>{y}</option>)}
                </Select>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setExportDialogType(null)}>Zrušit</Button>
              <Button
                appearance="primary"
                icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
                disabled={isExporting}
                onClick={handleExport}
              >
                {isExporting ? 'Připravuji…' : 'Stáhnout'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
